import React from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Icon,
  VStack,
  HStack,
  Badge,
  Divider,
  useColorModeValue,
} from "@chakra-ui/react";
import { 
  FiUsers, 
  FiDatabase, 
  FiSettings, 
  FiActivity,
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
  FiTrendingUp
} from "react-icons/fi";
import { ADMIN_THEME_CONFIG } from "../config";

// Dashboard stats interface
interface DashboardStats {
  totalUsers: number;
  totalCategories: number;
  totalEmployees: number;
  totalInspections: number;
  syncedInspections: number;
  pendingInspections: number;
  recentActivity: number;
}

// Mock data - replace with actual API calls
const mockStats: DashboardStats = {
  totalUsers: 150,
  totalCategories: 25,
  totalEmployees: 1200,
  totalInspections: 5480,
  syncedInspections: 5250,
  pendingInspections: 230,
  recentActivity: 45,
};

interface AdminDashboardOverviewProps {
  stats?: DashboardStats;
}

const AdminDashboardOverview: React.FC<AdminDashboardOverviewProps> = ({ 
  stats = mockStats 
}) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.600", "gray.400");

  const statCards = [
    {
      label: "Total Pengguna",
      value: stats.totalUsers,
      icon: FiUsers,
      color: "blue",
      helpText: "Pengguna aktif sistem",
      trend: { value: 12, type: "increase" as const }
    },
    {
      label: "Total Kategori",
      value: stats.totalCategories,
      icon: FiDatabase,
      color: "green",
      helpText: "Kategori inspeksi",
      trend: { value: 3, type: "increase" as const }
    },
    {
      label: "Total Karyawan",
      value: stats.totalEmployees.toLocaleString(),
      icon: FiUsers,
      color: "purple",
      helpText: "Karyawan terdaftar",
      trend: { value: 5, type: "increase" as const }
    },
    {
      label: "Total Inspeksi",
      value: stats.totalInspections.toLocaleString(),
      icon: FiActivity,
      color: "orange",
      helpText: "Inspeksi keseluruhan",
      trend: { value: 8, type: "increase" as const }
    },
    {
      label: "Inspeksi Terkirim",
      value: stats.syncedInspections.toLocaleString(),
      icon: FiCheckCircle,
      color: "green",
      helpText: "Data tersinkronisasi",
      trend: { value: 2, type: "increase" as const }
    },
    {
      label: "Inspeksi Pending",
      value: stats.pendingInspections,
      icon: FiAlertCircle,
      color: "red",
      helpText: "Menunggu sinkronisasi",
      trend: { value: 15, type: "decrease" as const }
    },
  ];

  const quickActions = [
    {
      title: "Manajemen Pengguna",
      description: "Kelola akses pengguna dan izin",
      icon: FiUsers,
      color: "blue",
      count: stats.totalUsers
    },
    {
      title: "Manajemen Kategori",
      description: "Kelola kategori inspeksi",
      icon: FiDatabase,
      color: "green",
      count: stats.totalCategories
    },
    {
      title: "Pengaturan Sistem",
      description: "Konfigurasi sistem",
      icon: FiSettings,
      color: "purple",
      count: null
    },
    {
      title: "Aktivitas Terkini",
      description: "Monitor aktivitas sistem",
      icon: FiTrendingUp,
      color: "orange",
      count: stats.recentActivity
    },
  ];

  return (
    <Box p={6}>
      {/* Header */}
      <VStack align="stretch" spacing={6} mb={8}>
        <Box>
          <Heading size="lg" color={ADMIN_THEME_CONFIG.COLORS.PRIMARY} mb={2}>
            Dashboard Admin
          </Heading>
          <Text color={textColor} fontSize="lg">
            Selamat datang di panel administrasi sistem. Kelola semua aspek sistem dari sini.
          </Text>
        </Box>
        
        <Divider borderColor={borderColor} />
      </VStack>

      {/* Statistics Cards */}
      <VStack align="stretch" spacing={6}>
        <Box>
          <Heading size="md" mb={4} color={textColor}>
            Statistik Sistem
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {statCards.map((stat) => (
              <Card
                key={stat.label}
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="lg"
                overflow="hidden"
                _hover={{ 
                  shadow: "lg", 
                  borderColor: `${stat.color}.200`,
                  transform: "translateY(-2px)"
                }}
                transition="all 0.2s"
              >
                <CardBody>
                  <Stat>
                    <Flex justify="space-between" align="flex-start">
                      <VStack align="start" spacing={2} flex="1">
                        <StatLabel color={textColor} fontSize="sm">
                          {stat.label}
                        </StatLabel>
                        <StatNumber 
                          fontSize="2xl" 
                          fontWeight="bold"
                          color={`${stat.color}.500`}
                        >
                          {stat.value}
                        </StatNumber>
                        <StatHelpText color={textColor} fontSize="xs" mb={0}>
                          {stat.helpText}
                          <StatArrow type={stat.trend.type} />
                          {stat.trend.value}%
                        </StatHelpText>
                      </VStack>
                      
                      <Box
                        p={3}
                        borderRadius="lg"
                        bg={`${stat.color}.50`}
                        color={`${stat.color}.500`}
                      >
                        <Icon as={stat.icon} boxSize={6} />
                      </Box>
                    </Flex>
                  </Stat>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </Box>

        {/* Quick Actions */}
        <Box>
          <Heading size="md" mb={4} color={textColor}>
            Aksi Cepat
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {quickActions.map((action) => (
              <Card
                key={action.title}
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="lg"
                overflow="hidden"
                cursor="pointer"
                _hover={{ 
                  shadow: "md", 
                  borderColor: `${action.color}.200`,
                  transform: "translateY(-1px)"
                }}
                transition="all 0.2s"
              >
                <CardBody>
                  <HStack spacing={4}>
                    <Box
                      p={3}
                      borderRadius="lg"
                      bg={`${action.color}.50`}
                      color={`${action.color}.500`}
                      flexShrink={0}
                    >
                      <Icon as={action.icon} boxSize={6} />
                    </Box>
                    
                    <VStack align="start" spacing={1} flex="1">
                      <HStack justify="space-between" w="100%">
                        <Text fontWeight="semibold" fontSize="md">
                          {action.title}
                        </Text>
                        {action.count !== null && (
                          <Badge 
                            colorScheme={action.color} 
                            variant="subtle"
                            borderRadius="full"
                            px={2}
                          >
                            {action.count}
                          </Badge>
                        )}
                      </HStack>
                      <Text color={textColor} fontSize="sm">
                        {action.description}
                      </Text>
                    </VStack>
                  </HStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </Box>

        {/* System Status */}
        <Box>
          <Heading size="md" mb={4} color={textColor}>
            Status Sistem
          </Heading>
          <Card
            bg={cardBg}
            border="1px solid"
            borderColor={borderColor}
            borderRadius="lg"
          >
            <CardHeader pb={2}>
              <HStack justify="space-between">
                <Text fontWeight="semibold">Status Sinkronisasi</Text>
                <Badge colorScheme="green" variant="subtle">
                  <Icon as={FiCheckCircle} mr={1} />
                  Aktif
                </Badge>
              </HStack>
            </CardHeader>
            <CardBody pt={2}>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <VStack>
                  <Icon as={FiCheckCircle} boxSize={8} color="green.500" />
                  <Text fontWeight="medium" textAlign="center">
                    {stats.syncedInspections.toLocaleString()} Terkirim
                  </Text>
                  <Text fontSize="sm" color={textColor} textAlign="center">
                    Data tersinkronisasi
                  </Text>
                </VStack>
                
                <VStack>
                  <Icon as={FiClock} boxSize={8} color="orange.500" />
                  <Text fontWeight="medium" textAlign="center">
                    {stats.pendingInspections} Pending
                  </Text>
                  <Text fontSize="sm" color={textColor} textAlign="center">
                    Menunggu sinkronisasi
                  </Text>
                </VStack>
                
                <VStack>
                  <Icon as={FiActivity} boxSize={8} color="blue.500" />
                  <Text fontWeight="medium" textAlign="center">
                    {((stats.syncedInspections / stats.totalInspections) * 100).toFixed(1)}%
                  </Text>
                  <Text fontSize="sm" color={textColor} textAlign="center">
                    Tingkat sinkronisasi
                  </Text>
                </VStack>
              </SimpleGrid>
            </CardBody>
          </Card>
        </Box>
      </VStack>
    </Box>
  );
};

export default AdminDashboardOverview;
